import { useState } from "react";
import { api } from "../../api";
import type { CommentStatus, Ticket } from "../../api/types";
import { useToast } from "../../compose/Toast";
import {
  applyMove,
  groupByStatus,
  KANBAN_COLUMNS,
  revertMove,
  type StatusOverrides,
} from "../../lib/kanban";
import { KanbanColumn } from "./KanbanColumn";

/**
 * The tickets KANBAN board (design tickets-kanban §4 + §6). Lays the 6 status
 * columns in a horizontal-scroll track and owns the OPTIMISTIC override map: a
 * move pins the card to its target column instantly, then persists through the
 * EXISTING `PATCH /comments/:cid/status` endpoint (`api.setCommentStatus` — ZERO
 * backend delta), reverting + toasting on failure. The fetched ticket array is
 * never mutated; the override map decides each card's column.
 */

/** Callback surface the board's move orchestration drives — split out so the
 *  exact optimistic-then-persist-then-revert logic unit-tests without a DOM. */
export interface MoveDeps {
  setStatus: (id: number, status: CommentStatus) => Promise<unknown>;
  applyOptimistic: (next: StatusOverrides) => void;
  revert: (id: number) => void;
  notifyError: (message: string) => void;
}

/**
 * Optimistic move (design §6): (1) guard a no-op self-drop; (2) pin the card to
 * the target column immediately; (3) persist through the existing status
 * endpoint; (4) on failure, snap back + toast. Pure orchestration over injected
 * callbacks — the board wires the real `api` + `setOverrides` + `toast`.
 */
export async function moveTicket(
  deps: MoveDeps,
  tickets: Ticket[],
  overrides: StatusOverrides,
  ticketId: number,
  target: CommentStatus,
): Promise<void> {
  const ticket = tickets.find((x) => x.id === ticketId);
  if (!ticket) return;
  const next = applyMove(overrides, ticketId, target, ticket.status);
  if (next === overrides) return; // no-op self-drop — no network write
  deps.applyOptimistic(next);
  try {
    await deps.setStatus(ticketId, target);
    // success → keep the optimistic override (design §6.1 reconcile).
  } catch {
    deps.revert(ticketId);
    deps.notifyError("Couldn't move this ticket — try again.");
  }
}

export function KanbanBoard({
  slug,
  tickets,
}: {
  slug: string;
  tickets: Ticket[];
}) {
  const [overrides, setOverrides] = useState<StatusOverrides>(() => new Map());
  const toast = useToast();

  const onMove = (ticketId: number, target: CommentStatus): void => {
    void moveTicket(
      {
        setStatus: api.setCommentStatus,
        applyOptimistic: setOverrides,
        revert: (id) => setOverrides((cur) => revertMove(cur, id)),
        notifyError: toast.show,
      },
      tickets,
      overrides,
      ticketId,
      target,
    );
  };

  const groups = groupByStatus(tickets, overrides);

  return (
    <div
      className="kanban-board"
      data-testid="kanban-board"
      role="list"
      aria-label="Tickets by status"
    >
      {KANBAN_COLUMNS.map((col) => (
        <KanbanColumn
          key={col.status}
          status={col.status}
          tickets={groups[col.status]}
          slug={slug}
          onMove={onMove}
        />
      ))}
    </div>
  );
}
